import { ObservableQuery } from "@apollo/client";
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

import usePolledQuery from "apollo/usePolledQuery";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import useShiftSelectionManager from "components/BulkActions/useShiftSelectionManager";
import DisabledStackNotice from "components/DisabledStackNotice";
import NotFoundPage from "components/error/NotFoundPage";
import FlashContext from "components/FlashMessages/FlashContext";
import { EmptystateRunsColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import StackLockNotice from "components/StackLockNotice";
import PageWrapper from "components/PageWrapper";
import InfiniteScroll from "components/scroll/InfiniteScroll";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useResumeInfiniteScrollOnRefetch from "hooks/useResumeInfiniteScrollOnRefetch";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { BillingTierFeature, Run, Stack } from "types/generated";
import { isAnsibleStackVendor } from "utils/stack";
import { uniqById } from "utils/uniq";
import { generateRepoUrlText } from "utils/urls";
import { providerHasPushEvents } from "utils/vcs";
import useTierFeature from "views/Account/hooks/useTierFeature";
import { AccountContext } from "views/AccountWrapper";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";

import { PollingIntervalGroups } from "../../../apollo/constants";
import StackDetachedIntegrationCallout from "../components/DetachedIntegrationCallout";
import StackHeader from "../components/Header";
import StackLockOrUnlockButton from "../components/LockOrUnlockButton";
import { RUNS_PER_PAGE } from "../constants";
import { StackContext } from "../Context";
import { getStacksBackUrl, groupPerCommitOnTimeline } from "../helpers";
import StackRunsBulkActions from "./BulkActions";
import SyncButton from "./components/SyncButton";
import TriggerButton from "./components/TriggerButton";
import StackRunsSelectAllDropdown from "./components/SelectAllDropdown";
import useStackRunsBulkSelection from "./useStackRunsBulkSelection";
import { LIST_RUNS } from "./gql";
import { getGroupKey } from "./helpers";
import StackRunsIgnoredRunCallout from "./IgnoredRunCallout";
import {
  getLastIgnoredRunFromStack,
  shouldShowIgnoredRunCallout,
} from "./IgnoredRunCallout/helpers";
import RunsCommitGroup from "./RunsCommitGroup";
import styles from "./styles.module.css";
import useHasAvailableRunsBulkActions from "./BulkActions/useHasAvailableBulkActions";

type TData = { stack: Stack };
type TVariables = {
  stackId: string;
  before?: string | null;
  hasIgnoredRunsAccess: boolean;
};

const Runs = () => {
  const isIgnoredRunsEnabled = useTierFeature(BillingTierFeature.RunIgnoredTriggers);

  const [hasError, setHasError] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { viewer } = useTypedContext(AccountContext);
  const { onError } = useTypedContext(FlashContext);
  const { stack: stackCtx, configurationManagementViewEnabled } = useTypedContext(StackContext);

  const listRef = useRef<HTMLDivElement>(null);

  useTitle(`Runs · ${stackCtx.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stackCtx.name,
    },
  ]);

  const { loading, error, data, fetchMore, refetch, networkStatus } = usePolledQuery<TData>(
    LIST_RUNS,
    {
      variables: {
        stackId: stackCtx.id,
        hasIgnoredRunsAccess: isIgnoredRunsEnabled,
      },
      pollingGroup: PollingIntervalGroups.Lists,
      shouldPoll: () => !hasError,
      // avoid request executing twice while fetchMore
      nextFetchPolicy: "cache-first",
    }
  );

  const updateQuery: ObservableQuery.FetchMoreOptions<TData, TVariables>["updateQuery"] = (
    previous,
    { fetchMoreResult }
  ) => {
    if (fetchMoreResult?.stack) {
      return {
        stack: {
          ...fetchMoreResult.stack,
          runs: uniqById([...fetchMoreResult.stack.runs, ...previous.stack.runs]).sort(
            (a, b) => b.createdAt - a.createdAt
          ),
        },
      };
    }

    return { stack: { ...previous.stack } };
  };

  const stack = useMemo(() => {
    if (data?.stack) {
      return {
        ...stackCtx,
        ...(data?.stack ? data.stack : {}),
      };
    }

    return undefined;
  }, [data, stackCtx]);

  const hasAvailableBulkActions = useHasAvailableRunsBulkActions();

  const runsListPerCommit = useMemo(() => {
    const groupedRuns = groupPerCommitOnTimeline<Run>(stack?.runs || []);
    const groupedRunsWithSelectable = groupedRuns.map(([hash, group], index) => {
      return { hash, group, selectable: hasAvailableBulkActions(group), index };
    });
    return groupedRunsWithSelectable;
  }, [stack?.runs, hasAvailableBulkActions]);

  const isAnyRunSelectable = useMemo(() => {
    return runsListPerCommit.some(({ selectable }) => selectable);
  }, [runsListPerCommit]);

  const handleScrollEnd = useCallback(async () => {
    const lastRun = stack?.runs[stack?.runs.length - 1];

    if (lastRun) {
      try {
        await fetchMore({
          variables: { before: lastRun.id },
          updateQuery,
        }).then(({ data: moreData }) => {
          if (moreData) {
            setHasMore(moreData.stack.runs.length === RUNS_PER_PAGE);
          }
        });
      } catch (err) {
        // force error rendering in result of using "cache-first"
        setHasError(true);
        onError(err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMore, stack]);

  const {
    allSelected,
    selectedSet,
    allRunsAreSelected,
    hasSomeSelected,
    fullySelectedGroups,
    selectItem,
    unselectItem,
    handleToggle,
    handleSelectOption,
    onBulkResetAll,
    onBulkContinueWith,
    syncAllSelectedItems,
    syncSelectedItemsWithVisibleOnes,
  } = useStackRunsBulkSelection({
    runsListPerCommit,
    hasMore,
  });

  const getSelection = useShiftSelectionManager({
    collection: stack?.runs,
    selectedIds: selectedSet,
  });

  const handleBulkResetAll = useCallback(() => {
    onBulkResetAll();
  }, [onBulkResetAll]);

  const onItemCheck = (item: Run, newChecked: boolean) => {
    const selectionIds = getSelection(item.id);
    if (newChecked) {
      selectionIds.forEach(selectItem);
    } else {
      selectionIds.forEach(unselectItem);
    }
  };

  const handleBulkActionsFinish = async () => {
    await refetch();
  };

  const handleGroupSelect = (groups: Run[], groupKey: string) => {
    const isFullySelected = fullySelectedGroups.has(groupKey);

    if (isFullySelected) {
      groups.forEach((run) => {
        unselectItem(run.id);
      });
    } else {
      groups.forEach((run) => {
        selectItem(run.id);
      });
    }
  };

  const runsMap = useMemo(() => {
    const runsEntities = data?.stack
      ? data.stack?.runs.map<[string, Run]>((run) => [run.id, run])
      : [];

    return new Map(runsEntities);
  }, [data]);

  // mark selected new loaded stacks if allSelected is true
  useEffect(() => {
    if (allSelected && stack?.runs) {
      syncAllSelectedItems(new Set(stack.runs.map((run) => run.id)));
    }
  }, [allSelected, stack?.runs, syncAllSelectedItems]);

  // sync the selected items with the visible items on the list (filter out the ones that are not visible)
  useEffect(() => {
    if (selectedSet.size) {
      syncSelectedItemsWithVisibleOnes(runsMap);
    }
  }, [runsMap, selectedSet.size, syncSelectedItemsWithVisibleOnes]);

  useResumeInfiniteScrollOnRefetch(networkStatus, () => setHasMore(true));

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  // Do not show 'loading' when polling in the background.
  if (loading && !data?.stack) {
    return <PageLoading />;
  }

  if (!data?.stack || !stack) {
    return <NotFoundPage />;
  }

  const mostRecentRun = stack.runs?.[0];
  const lastIgnoredRun = getLastIgnoredRunFromStack(stack);

  const showIgnoredRunCallout = shouldShowIgnoredRunCallout(stack, lastIgnoredRun, mostRecentRun);
  const isAnsible = configurationManagementViewEnabled && isAnsibleStackVendor(stack);

  return (
    <>
      <div className={styles.stickyHeader}>
        <StackHeader noSticky={true} />
        <PageInfoHeader
          className={styles.pageInfoHeaderWrapper}
          title={
            <Box direction="row" align="center" gap="medium">
              {isAnyRunSelectable && (
                <StackRunsSelectAllDropdown
                  isSelected={allRunsAreSelected}
                  isIndeterminate={hasSomeSelected}
                  onToggle={handleToggle}
                  onSelectOption={handleSelectOption}
                />
              )}
              <PageInfoHeaderTitle title="Tracked runs" />
            </Box>
          }
        >
          <SyncButton />
          <StackLockOrUnlockButton />
          <TriggerButton stack={stack} text="Trigger" allowCustomConfig />
        </PageInfoHeader>
      </div>

      <StackDetachedIntegrationCallout />
      {showIgnoredRunCallout && (
        <StackRunsIgnoredRunCallout stack={stack} ignoredRun={lastIgnoredRun} />
      )}
      {stack.lockedBy !== null && (
        <StackLockNotice
          lockedAt={stack.lockedAt}
          lockedBy={stack.lockedBy}
          viewer={viewer.id}
          note={stack.lockNote}
        />
      )}
      <PageWrapper>
        <InfiniteScroll onScrollEnd={handleScrollEnd} hasMore={hasMore} loading={loading}>
          {(stack.isDisabled || stack.vcsDetached) && <DisabledStackNotice />}

          {runsListPerCommit.length > 0 && (
            <Box ref={listRef} gap="large" direction="column">
              {runsListPerCommit.map(({ hash, group, selectable, index }) => (
                <RunsCommitGroup
                  key={getGroupKey(hash, index)}
                  hash={hash}
                  group={group}
                  selectable={selectable}
                  index={index}
                  stack={stack}
                  isAnsible={isAnsible}
                  selectedSet={selectedSet}
                  fullySelectedGroups={fullySelectedGroups}
                  onItemCheck={onItemCheck}
                  onGroupSelect={handleGroupSelect}
                />
              ))}
            </Box>
          )}

          {stack.runs && stack.runs.length === 0 && providerHasPushEvents(stack.provider) && (
            <EmptyState
              icon={EmptystateRunsColored}
              title="No runs yet"
              caption={
                <>
                  {" "}
                  As you push new commits to the <em>{stack.branch}</em> branch of the{" "}
                  <em>
                    {generateRepoUrlText({
                      namespace: stack.namespace,
                      repository: stack.repository,
                    })}
                  </em>{" "}
                  Git repository, new Runs will appear on this list.{" "}
                  {stack.canWrite && (
                    <>
                      You can also start one now manually by clicking on the <em>Trigger</em> button
                      above.
                    </>
                  )}
                </>
              }
            />
          )}
          {stack.runs && stack.runs.length === 0 && !providerHasPushEvents(stack.provider) && (
            <EmptyState
              icon={EmptystateRunsColored}
              title="No runs yet"
              caption={
                <>
                  You can start a run now manually by clicking on the <em>Trigger</em> button above.
                </>
              }
            />
          )}
          {isAnyRunSelectable && (
            <StackRunsBulkActions
              listRef={listRef as RefObject<HTMLElement | null>}
              selectedSet={selectedSet}
              runsMap={runsMap}
              stack={stack}
              onBulkResetAll={handleBulkResetAll}
              onBulkContinueWith={onBulkContinueWith}
              onItemDismiss={(id) => {
                const run = runsMap.get(id);
                if (run) {
                  onItemCheck(run, false);
                } else {
                  unselectItem(id);
                }
              }}
              onFinish={handleBulkActionsFinish}
            />
          )}
        </InfiniteScroll>
      </PageWrapper>
    </>
  );
};

export default Runs;
